import React, { Children, useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { tokens } from "../../theme/galleryTheme";

const GAP = { xs: 16, sm: 24, md: 32 }; // px

/** CSS width of one card when `n` cards (and n-1 gaps) must fit the row. */
const colWidth = (n, gap) => (n <= 1 ? "100%" : `calc((100% - ${(n - 1) * gap}px) / ${n})`);


export default function CardRail({ children, label, perView = { sm: 2, md: 3, lg: 4 }, mobileWidth = "82%" }) {
  const railRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const items = Children.toArray(children).filter(Boolean);

  const update = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft < max - 4);
  }, []);

  useEffect(() => {
    update();
    const el = railRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update, items.length]);

  const scrollByPage = (direction) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };

  const showControls = canPrev || canNext;

  return (
    <Box>
      <Box
        ref={railRef}
        role="list"
        aria-label={label}
        tabIndex={0}
        sx={{
          display: "grid",
          gridAutoFlow: "column",
          gridAutoColumns: {
            xs: mobileWidth,
            sm: colWidth(perView.sm || 2, GAP.sm),
            md: colWidth(perView.md || 3, GAP.md),
            lg: colWidth(perView.lg || perView.md || 3, GAP.md),
          },
          columnGap: { xs: `${GAP.xs}px`, sm: `${GAP.sm}px`, md: `${GAP.md}px` },
          overflowX: "auto",
          overscrollBehaviorX: "contain",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "thin",
          pb: 2,
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: tokens.color.line },
        }}
      >
        {items.map((child, index) => (
          <Box key={child.key ?? index} role="listitem" sx={{ scrollSnapAlign: "start", minWidth: 0 }}>
            {child}
          </Box>
        ))}
      </Box>

      {showControls && (
        <Box sx={{ display: { xs: "none", sm: "flex" }, justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <RailButton label={`Previous ${label}`} disabled={!canPrev} onClick={() => scrollByPage(-1)}>
            <ArrowBackIcon fontSize="small" />
          </RailButton>
          <RailButton label={`Next ${label}`} disabled={!canNext} onClick={() => scrollByPage(1)}>
            <ArrowForwardIcon fontSize="small" />
          </RailButton>
        </Box>
      )}
    </Box>
  );
}

function RailButton({ label, disabled, onClick, children }) {
  return (
    <IconButton
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 44,
        height: 44,
        border: `1px solid ${tokens.color.ink}`,
        color: tokens.color.ink,
        "&:hover": { bgcolor: tokens.color.ink, color: "#fff" },
        "&.Mui-disabled": { borderColor: tokens.color.line, color: "#9A9A9A" },
      }}
    >
      {children}
    </IconButton>
  );
}
